import { useRef, useState } from "react";
import api from "@/lib/api";
import { openAuthedFile } from "@/lib/files";
import { useVacLibrary, frDate } from "@/lib/vac";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BookMarked, Upload, Eye, Trash2, RefreshCw, Loader2, Search } from "lucide-react";

const inputCls = "mt-1 border-slate-700 bg-slate-900/60 text-slate-100";

function UploadForm({ onDone, existing }) {
  const [icao, setIcao] = useState("");
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const replacing = existing[icao.toUpperCase()];

  const submit = async () => {
    if (!/^[A-Z0-9]{4}$/i.test(icao)) return toast.error("Code OACI invalide (4 caractères, ex. LFPN)");
    if (!file) return toast.error("Choisissez le fichier de la carte");
    const fd = new FormData();
    fd.append("icao", icao.toUpperCase());
    fd.append("name", name);
    fd.append("edition_date", date);
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/vac-charts", fd);
      toast.success(replacing ? `Carte ${icao.toUpperCase()} remplacée par la nouvelle édition` : `Carte ${icao.toUpperCase()} ajoutée`);
      setIcao(""); setName(""); setDate(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass rounded-2xl p-6" data-testid="vac-upload-form">
      <h2 className="mb-4 flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-cyan-400">
        <Upload className="h-4 w-4" /> Ajouter une carte VAC
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-400">Code OACI</Label>
          <Input value={icao} maxLength={4} onChange={(e) => setIcao(e.target.value.toUpperCase())} placeholder="LFPN"
            className={`${inputCls} font-mono-data`} data-testid="vac-icao-input" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-400">Aérodrome (optionnel)</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Toussus-le-Noble" className={inputCls} data-testid="vac-name-input" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-400">Date d'édition</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="vac-date-input" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-400">Fichier (PDF, JPG, PNG)</Label>
          <Input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)}
            className={`${inputCls} file:text-cyan-300`} data-testid="vac-file-input" />
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={submit} disabled={busy} data-testid="vac-upload-submit" className="bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} Enregistrer
        </Button>
        {replacing && (
          <span className="text-xs text-amber-300" data-testid="vac-replace-warning">
            {replacing.icao} existe déjà (édition du {frDate(replacing.edition_date)}) : elle sera remplacée par cette nouvelle édition.
          </span>
        )}
      </div>
    </div>
  );
}

function VacRow({ c, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const newEdition = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const date = window.prompt(`Date d'édition de la nouvelle carte ${c.icao} (AAAA-MM-JJ)`, new Date().toISOString().slice(0, 10));
    if (date === null) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("edition_date", date);
    setBusy(true);
    try { await api.post(`/vac-charts/${c.id}/edition`, fd); toast.success(`Nouvelle édition ${c.icao} enregistrée`); onChange(); }
    catch (err) { toast.error(err.response?.data?.detail || "Échec du remplacement"); }
    finally { setBusy(false); }
  };
  const setDate = async (v) => { await api.put(`/vac-charts/${c.id}`, { edition_date: v }); onChange(); };
  const remove = async () => {
    if (!window.confirm(`Supprimer la carte VAC ${c.icao} ?`)) return;
    await api.delete(`/vac-charts/${c.id}`);
    toast.success("Carte supprimée");
    onChange();
  };
  return (
    <tr className="border-b border-slate-800" data-testid={`vac-row-${c.icao}`}>
      <td className="px-4 py-3 font-mono-data text-base font-bold text-cyan-300">{c.icao}</td>
      <td className="px-4 py-3 text-sm text-slate-300">{c.name || "—"}</td>
      <td className="px-4 py-3">
        <input type="date" defaultValue={c.edition_date} onBlur={(e) => e.target.value !== c.edition_date && setDate(e.target.value)}
          className="rounded-md border border-slate-700 bg-slate-900/60 px-2 py-1 text-sm text-slate-100" data-testid={`vac-edition-${c.icao}`} />
      </td>
      <td className="max-w-[220px] truncate px-4 py-3 text-xs text-slate-500">{c.original_filename}</td>
      <td className="px-4 py-3">
        <div className="flex justify-end gap-1">
          <button onClick={() => openAuthedFile(`/vac-charts/${c.id}/download`)} title="Voir" data-testid={`vac-view-${c.icao}`}
            className="rounded p-1.5 text-slate-400 hover:bg-slate-800 hover:text-cyan-300"><Eye className="h-4 w-4" /></button>
          <label title="Nouvelle édition" className="cursor-pointer rounded p-1.5 text-slate-400 hover:bg-slate-800 hover:text-amber-300">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={newEdition} data-testid={`vac-replace-${c.icao}`} />
          </label>
          <button onClick={remove} title="Supprimer" data-testid={`vac-delete-${c.icao}`}
            className="rounded p-1.5 text-slate-400 hover:bg-rose-500/10 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button>
        </div>
      </td>
    </tr>
  );
}

export default function VacLibraryPage() {
  const { charts, loading, reload, byCode } = useVacLibrary();
  const [q, setQ] = useState("");
  const shown = charts.filter((c) => `${c.icao} ${c.name || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-6" data-testid="vac-library-page">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-cyan-500/80"><BookMarked className="h-3.5 w-3.5" /> Bibliothèque</div>
        <h1 className="mt-1 font-display text-3xl font-bold text-slate-100">Cartes VAC</h1>
        <p className="mt-1 text-sm text-slate-400">Une carte par aérodrome (code OACI). Une nouvelle édition remplace l'ancienne ; les briefings utilisent toujours la dernière.</p>
      </div>
      <UploadForm onDone={reload} existing={byCode} />
      <div className="glass overflow-hidden rounded-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-slate-800 px-4 py-3">
          <span className="text-sm text-slate-300" data-testid="vac-count">{charts.length} carte{charts.length > 1 ? "s" : ""}</span>
          <div className="relative w-56">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="h-9 border-slate-700 bg-slate-900/60 pl-8 text-sm text-slate-100" data-testid="vac-search" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left">
            <thead>
              <tr className="border-b border-slate-800 text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2">OACI</th><th className="px-4 py-2">Aérodrome</th><th className="px-4 py-2">Édition</th><th className="px-4 py-2">Fichier</th><th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>{shown.map((c) => <VacRow key={`${c.id}-${c.updated_at}`} c={c} onChange={reload} />)}</tbody>
          </table>
        </div>
        {!loading && charts.length === 0 && <p className="p-6 text-sm text-slate-500" data-testid="vac-empty">Aucune carte. Ajoutez votre première carte VAC ci-dessus.</p>}
      </div>
    </div>
  );
}
